import React, { useState } from "react"

import { Alert, Button, RadioGroup, RadioGroupItem } from "@coval/ui"

import {
  FIELD_CONTAINER_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
} from "../styles"

export type CustomTraceValueSource = "attribute" | "duration"

interface CustomTraceValueSourceFieldProps {
  value: CustomTraceValueSource
  metricAttribute: string
  onChange: (value: CustomTraceValueSource) => void
}

const VALUE_SOURCE_OPTIONS: { value: CustomTraceValueSource; label: string }[] =
  [
    { value: "attribute", label: "Span attribute" },
    { value: "duration", label: "Span duration" },
  ]

/**
 * Chooses between aggregating a span attribute and the span's own duration.
 * Switching to duration discards the Metric Attribute, so when one is set the
 * switch waits for an explicit confirmation. The confirmation is bound to the
 * attribute it asked about: once the record shows another attribute, or is
 * already on duration, it no longer applies.
 */
export const CustomTraceValueSourceField: React.FC<
  CustomTraceValueSourceFieldProps
> = ({ value, metricAttribute, onChange }) => {
  const [confirmingAttribute, setConfirmingAttribute] = useState<string | null>(
    null,
  )
  const labelId = React.useId()
  const attributeToDiscard = metricAttribute.trim()
  const isConfirmingDuration =
    value !== "duration" &&
    confirmingAttribute !== null &&
    confirmingAttribute === attributeToDiscard

  const handleValueChange = (nextValue: string) => {
    if (nextValue !== "attribute" && nextValue !== "duration") {
      return
    }
    if (
      nextValue === "duration" &&
      value !== "duration" &&
      attributeToDiscard
    ) {
      setConfirmingAttribute(attributeToDiscard)
      return
    }
    setConfirmingAttribute(null)
    onChange(nextValue)
  }

  return (
    <div className={FIELD_CONTAINER_STYLES}>
      <div className={LABEL_ROW_STYLES}>
        <span id={labelId} className={LABEL_STYLES}>
          Aggregate
        </span>
      </div>
      <RadioGroup
        value={value}
        onValueChange={handleValueChange}
        aria-labelledby={labelId}
        className="flex gap-4"
      >
        {VALUE_SOURCE_OPTIONS.map((option) => (
          <div key={option.value} className="flex items-center gap-2">
            <RadioGroupItem
              value={option.value}
              id={`value-source-${option.value}`}
              aria-label={option.label}
            />
            <label
              htmlFor={`value-source-${option.value}`}
              className="text-sm cursor-pointer"
            >
              {option.label}
            </label>
          </div>
        ))}
      </RadioGroup>
      {isConfirmingDuration ? (
        <Alert
          variant="warning"
          className="mt-2"
          data-testid="custom-trace-duration-switch-warning"
        >
          <p>
            Span duration measures how long each span lasts, in seconds. It
            doesn&apos;t read any attribute, so switching discards the Metric
            Attribute {attributeToDiscard}.
          </p>
          <div className="mt-2 flex gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setConfirmingAttribute(null)
                onChange("duration")
              }}
            >
              Switch to Span duration
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setConfirmingAttribute(null)}
            >
              Keep Span attribute
            </Button>
          </div>
        </Alert>
      ) : value === "duration" ? (
        <p className="mt-1 text-xs text-coval-text-muted">
          Measures how long each span lasts, in seconds. No attribute is read.
        </p>
      ) : null}
    </div>
  )
}
